import { useEffect, useMemo, useState } from "react";
import { CreateNovedadRequest, Novedad } from "@services/NovedadesService";

interface NovedadEditFormState {
    name: string;
    payrollCode: string;
    backgroundColor: string;
    foregroundColor: string;
    isUsableInLicenses: boolean;
}

interface NovedadEditFormErrors {
    name?: string;
    backgroundColor?: string;
    foregroundColor?: string;
}

const hexColorPattern = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const normalizeOptionalField = (value: string) => {
    const trimmedValue = value.trim();
    return trimmedValue === "" ? undefined : trimmedValue;
};

const buildFormState = (novedad: Novedad | null): NovedadEditFormState => ({
    name: novedad?.name ?? "",
    payrollCode: novedad?.payrollCode ?? "",
    backgroundColor: novedad?.backgroundColor ?? "",
    foregroundColor: novedad?.foregroundColor ?? "",
    isUsableInLicenses: Boolean(novedad?.isUsableInLicenses),
});

export const useNovedadEditModalViewModel = (novedad: Novedad | null, isOpen: boolean) => {
    const [formState, setFormState] = useState<NovedadEditFormState>(buildFormState(novedad));

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setFormState(buildFormState(novedad));
    }, [isOpen, novedad]);

    const errors = useMemo<NovedadEditFormErrors>(() => {
        const nextErrors: NovedadEditFormErrors = {};

        if (formState.name.trim() === "") {
            nextErrors.name = "La descripción es obligatoria".localized();
        }

        if (formState.backgroundColor.trim() !== "" && !hexColorPattern.test(formState.backgroundColor.trim())) {
            nextErrors.backgroundColor = "Usá un color hexadecimal válido, por ejemplo #EC6C56".localized();
        }

        if (formState.foregroundColor.trim() !== "" && !hexColorPattern.test(formState.foregroundColor.trim())) {
            nextErrors.foregroundColor = "Usá un color hexadecimal válido, por ejemplo #FFFFFF".localized();
        }

        return nextErrors;
    }, [formState.backgroundColor, formState.foregroundColor, formState.name]);

    const initialComparableState = useMemo(() => buildFormState(novedad), [novedad]);

    const hasChanges = useMemo(() => {
        return formState.name.trim() !== initialComparableState.name.trim()
            || formState.payrollCode.trim() !== initialComparableState.payrollCode.trim()
            || formState.backgroundColor.trim() !== initialComparableState.backgroundColor.trim()
            || formState.foregroundColor.trim() !== initialComparableState.foregroundColor.trim()
            || formState.isUsableInLicenses !== initialComparableState.isUsableInLicenses;
    }, [formState, initialComparableState]);

    const canSave = novedad !== null && Object.keys(errors).length === 0 && hasChanges;

    const payload = useMemo<CreateNovedadRequest | null>(() => {
        if (!novedad) {
            return null;
        }

        return {
            novedad: formState.name.trim(),
            novedadAcronym: novedad.acronym,
            payrollCode: normalizeOptionalField(formState.payrollCode),
            backgroundColor: normalizeOptionalField(formState.backgroundColor),
            foregroundColor: normalizeOptionalField(formState.foregroundColor),
        };
    }, [formState.backgroundColor, formState.foregroundColor, formState.name, formState.payrollCode, novedad]);

    return {
        formState,
        errors,
        canSave,
        payload,
        setName: (name: string) => setFormState((previousState) => ({ ...previousState, name })),
        setPayrollCode: (payrollCode: string) => setFormState((previousState) => ({ ...previousState, payrollCode })),
        setBackgroundColor: (backgroundColor: string) => setFormState((previousState) => ({ ...previousState, backgroundColor })),
        setForegroundColor: (foregroundColor: string) => setFormState((previousState) => ({ ...previousState, foregroundColor })),
        setIsUsableInLicenses: (isUsableInLicenses: boolean) => setFormState((previousState) => ({
            ...previousState,
            isUsableInLicenses,
        })),
    };
};

export default useNovedadEditModalViewModel;
